/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * API Client factory — fetch wrapper with correlation IDs, dedupe, and 429 retry.
 * Load-bearing: retry envelope (withRetry defaults), GET dedupe + retry.enabled defaults,
 * failure/success shapes from api-client-helpers (bit-identical to pre-split).
 */

import { getSupabaseToken } from "@/lib/auth/token-utils";
import { createTimeoutController, getTimeout } from "@/lib/config/api-timeouts";
import { withRetry } from "@/lib/utils/resilience/retry";
import { logger } from "../../utils/logger";
import {
  cleanupCache,
  getCachedRequest,
  getGetRequestDedupeTtlMs,
  getRequestKey,
  setCachedRequest,
} from "../api-client-cache";
import { createUnauthorizedResponse } from "../api-client-helpers";
import type { ApiClientConfig, ApiResult, RequestOptions } from "../api-client-types";
import { executeRequest, type RequestOptionsInternal } from "./execute-request";

export type ApiClient = ReturnType<typeof createApiClient>;

export function createApiClient(config: ApiClientConfig): {
  request: <TResponse, TBody = unknown>(
    path: string,
    options?: RequestOptions<TBody>,
  ) => Promise<ApiResult<TResponse>>;
  get: <T>(
    path: string,
    headers?: Record<string, string>,
  ) => Promise<ApiResult<T>>;
  post: <TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    headers?: Record<string, string>,
  ) => Promise<ApiResult<TResponse>>;
  put: <TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    headers?: Record<string, string>,
  ) => Promise<ApiResult<TResponse>>;
  del: <TResponse>(
    path: string,
    headers?: Record<string, string>,
  ) => Promise<ApiResult<TResponse>>;
  delete: <TResponse>(
    path: string,
    headers?: Record<string, string>,
  ) => Promise<ApiResult<TResponse>>;
  patch: <TResponse, TBody = unknown>(
    path: string,
    body?: TBody,
    headers?: Record<string, string>,
  ) => Promise<ApiResult<TResponse>>;
} {
  const baseUrl = config.baseUrl?.replace(/\/$/, "") ?? "";

  async function buildHeaders(
    userHeaders?: Record<string, string>,
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(userHeaders ?? {}),
    };
    const token = await getSupabaseToken(config.getToken);
    if (token && !headers["Authorization"]) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const orgId = config.getOrganizationId
      ? config.getOrganizationId()
      : undefined;
    if (orgId && !headers["X-Organization-Id"]) {
      headers["X-Organization-Id"] = orgId;
    }
    return headers;
  }

  async function prepareRequest(
    path: string,
    options: RequestOptionsInternal,
  ): Promise<{
    url: string;
    init: RequestInit & { signal?: AbortSignal | null; body?: string | null };
    timeoutController: ReturnType<typeof createTimeoutController> | null;
  } | null> {
    const method = options.method ?? "GET";
    const url = path.startsWith("http") ? path : `${baseUrl}${path}`;
    const requireAuth = config.requireAuth ?? method !== "GET";

    // Check token presence before making request if auth is required
    if (requireAuth) {
      const token = await getSupabaseToken(config.getToken);
      if (!token) {
        logger.error(
          "[ApiClient] Request blocked - missing authentication token",
          { path, method, requireAuth, possibleTimeout: true },
        );
        return null;
      }
    }

    logger.info("[ApiClient] About to call buildHeaders", { path, url });

    const headers = await buildHeaders(options.headers);
    logger.info("[ApiClient] buildHeaders completed", {
      path,
      hasHeaders: !!headers,
      headerKeys: headers ? Object.keys(headers) : [],
    });

    // Determine timeout
    const isExternal = path.startsWith("http");
    const timeout =
      options.timeout ??
      (isExternal
        ? getTimeout("external", "DEFAULT")
        : getTimeout("internal", "DEFAULT"));

    const timeoutController = options.signal
      ? null
      : createTimeoutController(timeout);
    const signal = options.signal ?? timeoutController?.signal ?? null;

    const init: RequestInit & {
      signal?: AbortSignal | null;
      body?: string | null;
    } = {
      method,
      headers,
      signal,
      // SECURITY: Prevent browser HTTP cache from serving stale cross-org data.
      // Without this, the browser caches API responses by URL and serves them
      // even after org switches, causing multi-tenant data leakage.
      cache: "no-store" as RequestCache,
    };

    if (options.body !== undefined && method !== "GET") {
      init.body = JSON.stringify(options.body);
    } else if (method === "GET") {
      init.body = null;
    }

    return { url, init, timeoutController };
  }

  async function request<T>(
    path: string,
    options?: RequestOptionsInternal,
  ): Promise<ApiResult<T>>;
  async function request<T, TBody = unknown>(
    path: string,
    options?: RequestOptionsInternal<TBody>,
  ): Promise<ApiResult<T>>;
  async function request<T, TBody = unknown>(
    path: string,
    options: RequestOptionsInternal<TBody> = {},
  ): Promise<ApiResult<T>> {
    try {
      logger.info("[ApiClient] request() function called", {
        path,
        method: options.method,
        hasBody: !!options.body,
      });

      // Cleanup old cache entries periodically
      if (Math.random() < 0.1) {
        cleanupCache();
      }

      const method = options.method ?? "GET";

      // GET request deduplication
      // Skip dedup when caller provides their own signal (e.g. AbortController
      // from React effects) — the cached promise may have been aborted by a
      // previous effect cleanup (React Strict Mode) and returning it would
      // give the new caller a stale TIMEOUT result.
      if (method === "GET" && !options.signal) {
        const requestKey = getRequestKey(path, method, options.body);
        const dedupeTtlMs = getGetRequestDedupeTtlMs(path);
        const cached = getCachedRequest<T>(requestKey);

        if (cached) {
          logger.debug("[ApiClient] Deduplicating duplicate request", {
            path,
            method,
            dedupeTtlMs,
          });
          return cached;
        }

        // Create and store promise BEFORE token fetch for deduplication
        const requestPromise = (async (): Promise<ApiResult<T>> => {
          const prepared = await prepareRequest(path, options);
          if (!prepared) {
            return createUnauthorizedResponse();
          }

          const { url, init, timeoutController } = prepared;

          const makeRequest = () =>
            executeRequest<T>(
              baseUrl,
              config,
              url,
              init,
              timeoutController,
              path,
              options,
              (p, o) => request<T>(p, o),
            );

          const retryEnabled = options.retry?.enabled ?? true;
          logger.info("[ApiClient] Creating request promise", {
            url,
            method: init.method,
            retryEnabled,
          });

          return retryEnabled
            ? withRetry(makeRequest, {
                maxAttempts: options.retry?.maxAttempts ?? 3,
                baseDelay: options.retry?.baseDelay ?? 1000,
              })
            : makeRequest();
        })();

        setCachedRequest(requestKey, requestPromise, dedupeTtlMs);
        return requestPromise;
      }

      // Non-GET requests (or GET with external signal): no deduplication
      const prepared = await prepareRequest(path, options);
      if (!prepared) {
        return createUnauthorizedResponse();
      }

      const { url, init, timeoutController } = prepared;

      const makeRequest = () =>
        executeRequest<T>(
          baseUrl,
          config,
          url,
          init,
          timeoutController,
          path,
          options,
          (p, o) => request<T>(p, o),
        );

      const retryEnabled = options.retry?.enabled ?? false;
      logger.info("[ApiClient] Creating request promise", {
        url,
        method: init.method,
        retryEnabled,
      });

      return retryEnabled
        ? withRetry(makeRequest, {
            maxAttempts: options.retry?.maxAttempts ?? 3,
            baseDelay: options.retry?.baseDelay ?? 1000,
          })
        : makeRequest();
    } catch (error) {
      logger.error("[ApiClient] request() function error", {
        path,
        method: options.method,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  }

  return {
    request,
    get: <T>(path: string, headers?: Record<string, string>) =>
      headers
        ? request<T>(path, { method: "GET", headers })
        : request<T>(path, { method: "GET" }),
    post: <T, TBody = unknown>(
      path: string,
      body?: TBody,
      headers?: Record<string, string>,
    ) => {
      logger.info("[ApiClient] post() method called", {
        path,
        hasBody: body !== undefined,
        hasHeaders: headers !== undefined,
      });
      return headers
        ? request<T>(path, { method: "POST", body, headers })
        : request<T>(path, { method: "POST", body });
    },
    put: <T, TBody = unknown>(
      path: string,
      body?: TBody,
      headers?: Record<string, string>,
    ) =>
      headers
        ? request<T>(path, { method: "PUT", body, headers })
        : request<T>(path, { method: "PUT", body }),
    /** @deprecated Use `delete` instead. */
    del: <T>(path: string, headers?: Record<string, string>) =>
      headers
        ? request<T>(path, { method: "DELETE", headers })
        : request<T>(path, { method: "DELETE" }),
    delete: <T>(path: string, headers?: Record<string, string>) =>
      headers
        ? request<T>(path, { method: "DELETE", headers })
        : request<T>(path, { method: "DELETE" }),
    patch: <T, TBody = unknown>(
      path: string,
      body?: TBody,
      headers?: Record<string, string>,
    ) =>
      headers
        ? request<T>(path, { method: "PATCH", body, headers })
        : request<T>(path, { method: "PATCH", body }),
  };
}
